import React, { useRef, useMemo } from 'react';
import { Box, Paper, TextField, IconButton, Typography, CircularProgress } from '@mui/material';
import { North as UpIcon, Close as CloseIcon } from '@mui/icons-material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { VersionData } from '../../../IdeationService';
//import { UploadedFile } from '../../../../../slices/ideationSlice/newIdeationSlice';
import { uploadFile } from '../../../../IdeationCOI/IdeationService';
import { getFileIcon, validateFile } from '../../../../../common/utils';
import ShowToast, { ToastTypeEnum } from '../../../../../common/components/ShowToast';
import { FileViewer } from '../../../../NewSolutionXPLR/modules/GeneralInformation/components/KeyInformation';
import { UploadedFile } from '../../../types';

interface MessageInputProps {
  inputMessage: string;
  setInputMessage: (msg: string) => void;
  isSending: boolean;
  contextState: any;
  setContextState: (updater: (prev: any) => any) => void;
  versionData: VersionData;
  filePreview: { url: string; fileName?: string } | null;
  setFilePreview: (val: { url: string; fileName?: string } | null) => void;
  setUploadingImage: (val: boolean) => void;
  onSend: (message: string) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Returns a unique file id */
const makeFileId = (prefix: string, index = 0) => `${prefix}-${Date.now()}-${index}`;

/** Returns the display name of a context process */
const getProcessNameById = (processId: string, contextProcesses?: any[]): string => {
  if (!contextProcesses) return `Context: ${processId}`;
  const found = contextProcesses.find((p: any) => p.id === processId);
  return found ? found.name : processId;
};

// ── File chip (shown in the input area before sending) ────────────────────────
const InputFileChip = ({
  file,
  onPreview,
  onRemove,
  isSending,
}: {
  file: UploadedFile & { isUploading?: boolean; isAnalyzing?: boolean; url?: string };
  onPreview: (url: string, name: string) => void;
  onRemove: (id: string) => void;
  isSending: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #E0E0E0',
      borderRadius: '12px',
      bgcolor: '#fafafa',
      px: 1,
      py: 0.5,
      minWidth: 180,
      maxWidth: '100%',
      opacity: file.isUploading || file.isAnalyzing ? 0.6 : 1,
      cursor: 'pointer',
    }}
    onClick={() => {
      if (file?.url) onPreview(file.url, file.name);
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '6px',
        bgcolor: '#f1f3f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        mr: 1,
      }}
    >
      {file.isUploading || file.isAnalyzing ? (
        <CircularProgress size={14} sx={{ color: '#5f6368' }} />
      ) : (
        getFileIcon(file.name, file.file)
      )}
    </Box>

    <Typography
      variant="body2"
      sx={{
        flex: 1,
        minWidth: 0,
        fontWeight: 500,
        fontSize: '14px',
        color: '#202124',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {file.name}
    </Typography>

    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onRemove(file.id);
      }}
      disabled={isSending || file.isUploading || file.isAnalyzing}
      sx={{
        ml: 0.5,
        color: '#5f6368',
        '&:hover': { bgcolor: 'transparent', color: '#000' },
        '&:disabled': { color: '#ccc' },
      }}
    >
      <CloseIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);

// ── Context chip ───────────────────────────────────────────────────────────────
const ContextChip = ({
  contextId,
  label,
  onRemove,
}: {
  contextId: string;
  label: string;
  onRemove: (id: string) => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      border: '1px solid #E0E0E0',
      borderRadius: '12px',
      bgcolor: '#fafafa',
      px: 1,
      py: 0.5,
      minWidth: 180,
      maxWidth: '100%',
    }}
  >
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '6px',
        bgcolor: '#f1f3f4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 1,
      }}
    >
      <AttachFileIcon sx={{ fontSize: 18, color: '#5f6368', transform: 'rotate(45deg)' }} />
    </Box>
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontWeight: 500,
        fontSize: '14px',
        color: '#202124',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <IconButton
      size="small"
      onClick={() => onRemove(contextId)}
      sx={{ ml: 0.5, color: '#5f6368', '&:hover': { bgcolor: 'transparent', color: '#000' } }}
    >
      <CloseIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);

// ── Main component ─────────────────────────────────────────────────────────────

const MessageInput = ({
  inputMessage,
  setInputMessage,
  isSending,
  contextState,
  setContextState,
  versionData,
  filePreview,
  setFilePreview,
  setUploadingImage,
  onSend,
}: MessageInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Derived state ──────────────────────────────────────────────
  const isSendDisabled = useMemo(() => {
    if (isSending) return true;
    if (contextState?.uploadedFiles?.some((f: any) => f?.isUploading)) return true;
    if (!inputMessage.trim() && !contextState?.uploadedFiles?.length) return true;
    return false;
  }, [inputMessage, contextState?.uploadedFiles, isSending]);

  const showWaitingBanner =
    !isSending &&
    inputMessage.trim() === '' &&
    !contextState?.uploadedFiles?.length &&
    !contextState?.selectedContexts?.length &&
    !Object.keys(versionData || {}).length;

  const visibleFiles = (contextState?.uploadedFiles || []).filter((f: any) => !f.isAnalyzing);
  const selectedContexts: string[] = contextState?.selectedContexts || [];

  // ── File helpers ───────────────────────────────────────────────
  const removeFile = (fileId: string) =>
    setContextState((prev: any) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((f: UploadedFile) => f.id !== fileId),
    }));

  const removeContext = (contextId: string) =>
    setContextState((prev: any) => ({
      ...prev,
      selectedContexts: prev.selectedContexts.filter((c: string) => c !== contextId),
    }));

  /** Uploads a single File object and updates contextState */
  const uploadSingleFile = async (tempFile: UploadedFile & { file: File }) => {
    try {
      const response = await uploadFile(tempFile.file);
      if (!response?.files?.length) {
        ShowToast({
          message: 'Something went wrong while uploading the file. Please try again',
          type: ToastTypeEnum.Error,
        });
        return;
      }
      if (response && response.files && response.files.length > 0) {
        setContextState((prev: any) => ({
          ...prev,
          uploadedFiles: prev.uploadedFiles.map((f: UploadedFile) =>
            f.id === tempFile.id
              ? {
                  ...f,
                  url: response.files[0],
                  isUploading: false,
                  filePath: response.files[0],
                  fileSize: `${Math.round(tempFile.file.size / 1024)}KB`,
                }
              : f
          ),
        }));
      } else {
        throw new Error('No file URL returned');
      }
    } catch {
      setContextState((prev: any) => ({
        ...prev,
        uploadedFiles: prev.uploadedFiles.filter((f: UploadedFile) => f.id !== tempFile.id),
      }));
      ShowToast({ message: `Upload failed for ${tempFile.name}`, type: ToastTypeEnum.Error });
    }
  };

  // ── Event handlers ─────────────────────────────────────────────
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(inputMessage);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const filesArray = Array.from(files) as File[];
    const validatedFiles: { file: File; index: number }[] = [];

    for (let i = 0; i < filesArray.length; i++) {
      const validation = await validateFile(filesArray[i]);
      if (!validation.ok) {
        ShowToast({
          message: `${filesArray[i].name}: ${validation.error}`,
          type: ToastTypeEnum.Error,
        });
      } else {
        validatedFiles.push({ file: filesArray[i], index: i });
      }
    }

    if (!validatedFiles.length) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const tempFiles = validatedFiles.map(({ file, index }) => ({
      id: makeFileId('file', index),
      name: file.name,
      file,
      isUploading: true,
    }));

    setContextState((prev: any) => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...tempFiles],
    }));

    setUploadingImage(true);

    for (const tempFile of tempFiles) {
      await uploadSingleFile(tempFile);
    }

    setUploadingImage(false);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = async (event: React.ClipboardEvent) => {
    if (!event.clipboardData) return;

    // Handle pasted image
    const imageItem = Array.from(event.clipboardData.items).find((item) =>
      item.type.startsWith('image/')
    );

    if (imageItem) {
      event.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;

      const validation = await validateFile(file);
      if (!validation.ok) {
        ShowToast({ message: validation?.error || '', type: ToastTypeEnum.Error });
        return;
      }

      const tempFile = {
        id: makeFileId('pasted'),
        name: file.name || 'pasted-image.png',
        file,
        isUploading: true,
      };

      setContextState((prev: any) => ({
        ...prev,
        uploadedFiles: [...prev.uploadedFiles, tempFile],
      }));

      setUploadingImage(true);
      await uploadSingleFile(tempFile);
      setUploadingImage(false);
      return;
    }

    // Handle pasted text with cursor position awareness
    const pastedText = event.clipboardData.getData('text');
    if (pastedText) {
      event.preventDefault();
      const input = event.target as HTMLTextAreaElement;
      const newValue =
        inputMessage.slice(0, input.selectionStart) +
        pastedText +
        inputMessage.slice(input.selectionEnd);
      setInputMessage(newValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        pt: 2,
        px: 2,
        pb: 1,
        backgroundColor: '#fff',
        borderRadius: '0 0 12px 12px',
      }}
    >
      {/* ── "Waiting for user input" banner ── */}
      {showWaitingBanner && (
        <Box
          sx={{
            backgroundColor: '#EDEEFF',
            mx: 0,
            p: 0.5,
            borderRadius: '8px 8px 0px 0px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <img src="/images/ai-xplr-logo-icon.svg" height={18} width={18} alt="xplr-logo" />
          <Typography sx={{ fontSize: '14px', color: '#1A55F9', fontWeight: 400 }}>
            Waiting for user input...
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        {/* ── Text input container ── */}
        <Box
          onClick={() => inputRef.current?.focus()}
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#fff',
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
            cursor: 'text',
          }}
        >
          {/* File + context chips */}
          {(visibleFiles.length > 0 || selectedContexts.length > 0) && !isSending && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
              {visibleFiles.map((file: any) => (
                <InputFileChip
                  key={file.id}
                  file={file}
                  onPreview={(url, name) => setFilePreview({ url, fileName: name })}
                  onRemove={removeFile}
                  isSending={isSending}
                />
              ))}
              {selectedContexts.map((contextId: string) => (
                <ContextChip
                  key={contextId}
                  contextId={contextId}
                  label={getProcessNameById(contextId, contextState.contextProcesses)}
                  onRemove={removeContext}
                />
              ))}
            </Box>
          )}

          {/* Text field */}
          <TextField
            inputRef={inputRef}
            multiline
            maxRows={3}
            placeholder="Message"
            variant="standard"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            disabled={isSending}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: '14px', p: 0 },
            }}
          />

          {/* Bottom row: attach + send */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}
          >
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={isSending}
              sx={{ backgroundColor: '#EEEEEE', p: '6px', borderRadius: '50%' }}
            >
              <AttachFileIcon sx={{ fontSize: 20, transform: 'rotate(45deg)' }} />
            </IconButton>

            <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileUpload} />

            <IconButton
              onClick={() => onSend(inputMessage)}
              disabled={isSendDisabled}
              sx={{
                backgroundColor: 'primary.main',
                color: '#fff',
                width: 28,
                height: 28,
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#1955F8' },
                '&.Mui-disabled': { backgroundColor: '#89a7fa' },
              }}
            >
              <UpIcon sx={{ color: '#fff', height: '18px', width: '18px' }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* File preview modal */}
      {filePreview && (
        <FileViewer
          fileUrl={filePreview.url}
          fileName={filePreview.fileName}
          onClose={() => setFilePreview(null)}
        />
      )}
    </Paper>
  );
};

export default MessageInput;
